import { useEffect, useRef } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import {
  Bold, Underline as UnderlineIcon, AlignRight, AlignCenter, AlignLeft, AlignJustify,
  List, ListOrdered, Heading2, Heading3,
} from 'lucide-react';
import { dv } from '../../i18n/dv.ts';
import { cn } from '../../lib/utils.ts';
import { usePageEstimate } from './usePageEstimate.ts';

// NOTE on Underline: TipTap v3's StarterKit already bundles the Underline
// mark (it did not in v2). The task brief has us add @tiptap/extension-underline
// as an explicit dependency for clarity/future-proofing, but registering it a
// second time here as a top-level extension would collide with StarterKit's
// own copy (duplicate extension name warning + last-one-wins). StarterKit's
// bundled Underline is used as-is; `toggleUnderline` below comes from it.

interface ToolbarButtonProps {
  active?: boolean;
  disabled?: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}

function ToolbarButton({ active, disabled, label, onClick, children }: ToolbarButtonProps): JSX.Element {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()} // keep editor selection focused
      onClick={onClick}
      className={cn(
        'inline-flex size-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed',
        active && 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]',
      )}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor }: { editor: Editor }): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-border bg-[hsl(var(--surface-low))] p-2" dir="rtl">
      <ToolbarButton
        label={dv.letters.bold}
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold size={18} strokeWidth={1.8} />
      </ToolbarButton>
      <ToolbarButton
        label={dv.letters.underline}
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon size={18} strokeWidth={1.8} />
      </ToolbarButton>

      <span className="mx-1 h-5 w-px bg-border" />

      <ToolbarButton
        label={dv.letters.heading2}
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 size={18} strokeWidth={1.8} />
      </ToolbarButton>
      <ToolbarButton
        label={dv.letters.heading3}
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 size={18} strokeWidth={1.8} />
      </ToolbarButton>

      <span className="mx-1 h-5 w-px bg-border" />

      <ToolbarButton
        label={dv.letters.alignRight}
        active={editor.isActive({ textAlign: 'right' })}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
      >
        <AlignRight size={18} strokeWidth={1.8} />
      </ToolbarButton>
      <ToolbarButton
        label={dv.letters.alignCenter}
        active={editor.isActive({ textAlign: 'center' })}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
      >
        <AlignCenter size={18} strokeWidth={1.8} />
      </ToolbarButton>
      <ToolbarButton
        label={dv.letters.alignLeft}
        active={editor.isActive({ textAlign: 'left' })}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
      >
        <AlignLeft size={18} strokeWidth={1.8} />
      </ToolbarButton>
      <ToolbarButton
        label={dv.letters.alignJustify}
        active={editor.isActive({ textAlign: 'justify' })}
        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
      >
        <AlignJustify size={18} strokeWidth={1.8} />
      </ToolbarButton>

      <span className="mx-1 h-5 w-px bg-border" />

      <ToolbarButton
        label={dv.letters.bulletList}
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List size={18} strokeWidth={1.8} />
      </ToolbarButton>
      <ToolbarButton
        label={dv.letters.orderedList}
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered size={18} strokeWidth={1.8} />
      </ToolbarButton>
    </div>
  );
}

interface LetterBodyEditorProps {
  value: string;
  onChange: (html: string) => void;
  readOnly?: boolean;
}

// RTL Faruma rich-text body editor for the letter content. `value` seeds the
// editor exactly ONCE on mount (init-once, matching LetterEditor.tsx's own
// pattern for the surrounding form fields) — TipTap owns the live document
// after that, and `onChange` streams HTML up into the same debounced
// autosave the rest of the field column already uses. The parent remounts
// this component via `key={letter.id}` when navigating between letters, so a
// genuinely different letter still gets a fresh editor instance/content.
export function LetterBodyEditor({ value, onChange, readOnly }: LetterBodyEditorProps): JSX.Element {
  const initialValueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    content: initialValueRef.current,
    editable: !readOnly,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        defaultAlignment: 'right',
      }),
    ],
    editorProps: {
      attributes: {
        dir: 'rtl',
        class: 'letter-body-prose',
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChangeRef.current(ed.getHTML());
    },
  });

  // Keep TipTap's `editable` flag in sync with readOnly (e.g. after
  // finalize) without re-seeding content.
  useEffect(() => {
    if (editor && editor.isEditable !== !readOnly) editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  useEffect(() => () => { editor?.destroy(); }, [editor]);

  const pages = usePageEstimate(editor?.getHTML() ?? initialValueRef.current);

  if (!editor) return <div className="input min-h-[16rem]" />;

  return (
    <div className="overflow-hidden rounded-2xl border border-input bg-background">
      {!readOnly && <Toolbar editor={editor} />}
      <EditorContent
        editor={editor}
        className="letter-body-editor max-h-[28rem] min-h-[16rem] overflow-y-auto px-4 py-5 text-sm sm:px-6"
      />
      <div className="flex items-center justify-start border-t border-border px-4 py-2.5">
        <span className="pill pill-neutral" dir="rtl">
          {dv.letters.pageEstimate.replace('{n}', String(pages))}
        </span>
      </div>
    </div>
  );
}
